import React, { useEffect, useState } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Image,
  CloseButton,
  Icon,
  useColorModeValue,
  Text,
  FormErrorMessage,
} from '@chakra-ui/react';
import { FaRegImage } from "react-icons/fa6";

const ImageUpload = ({
  label,
  onImageSelect,
  existingImage,
  isBase64 = false,
  validated = false,
  required = false,
  validateDimensions = false,
  width = 200,
  height = 200,
  fixWidth = null,
  fixHeight = null,
  setValidated = () => (true),
  setIsImageValid = () => (true),
}) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const brandStars = useColorModeValue('red.500', 'red.400');

  useEffect(() => {
    if (existingImage) {
      setSelectedImage(existingImage);
    }
    return () => {
      setSelectedImage(null);
    };
  }, [existingImage]);

  useEffect(() => {
    if (validated && !selectedImage) {
      setErrorMessage(`${label} is required.`);
    }
  }, [validated, selectedImage]);

  useEffect(() => {
    if (existingImage) {
      setSelectedImage(existingImage);

      if (validateDimensions) {
        const img = new window.Image();
        img.src = existingImage;
        img.onload = () => {
          if (fixHeight && fixWidth) {
            if (img.width !== fixWidth || img.height !== fixHeight) {
              setErrorMessage(`Image dimensions must be ${fixWidth}px × ${fixHeight}px.`);
              setIsImageValid(false);
            } else {
              setErrorMessage('');
              setIsImageValid(true);
            }
          } else {
            if (img.width !== img.height) {
              setErrorMessage('Image width and height must be the same.');
              setIsImageValid(false);
            } else if (img.width < width || img.height < height) {
              setErrorMessage(`Image dimensions must be at least ${width}px × ${height}px.`);
              setIsImageValid(false);
            } else {
              setErrorMessage('');
              setIsImageValid(true);
            }
          };
        }

        img.onerror = () => {
          setErrorMessage('Invalid image file.');
          setIsImageValid(false);
        };
      } else {
        setIsImageValid(true);
      }
    } else {
      setSelectedImage(null);
      setErrorMessage('');
    }
  }, [existingImage, validateDimensions, width, height]);

  const validateImageDimensions = (file) => {
    return new Promise((resolve, reject) => {
      if (!validateDimensions) {
        resolve(file);
        return;
      }

      const img = new window.Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(img.src);
        if (fixHeight && fixWidth) {
          if (img.width !== fixWidth || img.height !== fixHeight) {
            reject(`Image dimensions must be ${fixWidth}px × ${fixHeight}px.`);
          } else {
            resolve(file);
          }
        } else {
          if (img.width !== img.height) {
            reject('Image width and height must be the same.');
          } else if (img.width < width || img.height < height) {
            reject(`Image dimensions must be at least ${width}px × ${height}px.`);
          } else {
            resolve(file);
          }
        }
      };
      img.onerror = () => reject('Invalid image file.');
    });
  };

  const handleImageChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setErrorMessage('');

      try {
        await validateImageDimensions(file);
        setErrorMessage('');
        setIsImageValid(true);
      } catch (error) {
        setErrorMessage(String(error));
        setIsImageValid(false);
      }

      if (isBase64) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setSelectedImage(reader.result);
          if (onImageSelect) onImageSelect(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        const imageURL = URL.createObjectURL(file);
        setSelectedImage(imageURL);
        if (onImageSelect) onImageSelect(file);
      }
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    if (!required) {
      setIsImageValid(true)
    }
    if (required) {
      setValidated(false)
    }
    setErrorMessage(validated && required ? `${label} is required.` : '');
    const fileInput = document.getElementById('fileInput');
    if (fileInput) {
      fileInput.value = '';
    }
    if (onImageSelect) onImageSelect(null);
  };

  return (
    <FormControl isInvalid={!!errorMessage}>
      <FormLabel fontSize="l" fontWeight="900" mb="0" mr="10px" ms="4px">
        {label}
        {required && <Text as="span" color={brandStars}>*</Text>}
      </FormLabel>
      <Box
        position="relative"
        display="inline-block"
        cursor="pointer"
        borderRadius="md"
        height="120px"
        width="120px"
        overflow="visible"
        textAlign="center"
        onClick={() => document.getElementById('fileInput').click()}
      >
        {selectedImage ? (
          <Image
            src={selectedImage}
            alt="Selected"
            objectFit="contain"
            padding="5px"
            border={errorMessage ? '2px solid red' : '1px solid gray'}
            maxHeight='120px'
            maxWidth='120px'
            position='absolute'
            right={0}
          />
        ) : (
          <Box
            position="absolute"
            top="0"
            left="0"
            height="100%"
            width="100%"
            backgroundColor="gray.100"
          >
            <Icon as={FaRegImage} boxSize="100%" />
          </Box>
        )}

        {selectedImage && (
          <CloseButton
            height="10px"
            width="10px"
            position="absolute"
            top="-5px"
            right="-5px"
            background="white"
            borderRadius="full"
            boxShadow="md"
            onClick={(e) => {
              e.stopPropagation();
              handleRemoveImage();
            }}
            zIndex="1"
          />
        )}
      </Box>

      <Input
        id="fileInput"
        type="file"
        accept="image/*"
        onChange={handleImageChange}
        display="none"
      />

      {errorMessage && <FormErrorMessage mt={0}>{errorMessage}</FormErrorMessage>}
    </FormControl>
  );
};

export default ImageUpload;
